import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { T3AnalysisStore } from '../../../core/shared/t3-model/t3-analysis.store';
import { T3ApiResponse } from '../../../core/shared/t3-model/t3-response-model';

type PeriodLabel = '5 years' | '10 years' | '15 years' | '20 years';
type ImpactRow = { baseline: number; new: number; deltaAbs: number; deltaPct: number };
type HtImpactKey = 'undiagnosed' | 'diagnosed' | 'treated' | 'controlled';
type CascadeView = 'cascade' | 'scenarios';
type ScenarioMetric =
    | 'Controlled'
    | 'Treated'
    | 'Diagnosed'
    | 'Undiagnosed'
    | 'CVD_Events'
    | 'CVD_Patients'
    | 'CVD_Deaths';

type ScenarioGroup = 'total' | 'male' | 'female';

type HtImpactViewItem = {
    key: HtImpactKey;
    title: string;
    icon: string;
    row: ImpactRow;
};

@Component({
    selector: 'app-t3-projected-impact',
    standalone: true,
    imports: [CommonModule, FormsModule, ChartModule, SelectModule],
    templateUrl: './t3-projected-impact.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class T3ProjectedImpactComponent implements OnInit, OnChanges, OnDestroy {
    private store = inject(T3AnalysisStore);
    private cdr = inject(ChangeDetectorRef);
    private destroyed$ = new Subject<void>();

    private scenarioXMin = 2026;
    private scenarioXMax = 2046;
    private scenarioSuggestedMax = 1;

    @Input() selectedInfoSimulation = 'Basic Simulation';
    @Input() selectedInfoPeriod: PeriodLabel = '5 years';
    @Input() selectedCascadeSimulation = 'Basic Simulation';

    cascadeView: CascadeView = 'scenarios';
    htCvdView: 'ht' | 'cvd' = 'ht';

    cascadeData: any = { labels: [], datasets: [] };
    cascadeOptions: any = {};

    scenarioChartData: any = { labels: [], datasets: [] };
    scenarioChartOptions: any = {};

    scenarioMetric: ScenarioMetric = 'Controlled';
    scenarioGroup: ScenarioGroup = 'total';

    readonly scenarioMetricOptions: Array<{ label: string; value: ScenarioMetric }> = [
        { label: 'Controlled', value: 'Controlled' },
        { label: 'Treated', value: 'Treated' },
        { label: 'Diagnosed', value: 'Diagnosed' },
        { label: 'Undiagnosed', value: 'Undiagnosed' },
        { label: 'CVD Events', value: 'CVD_Events' },
        { label: 'CVD Patients', value: 'CVD_Patients' },
        { label: 'CVD Deaths', value: 'CVD_Deaths' }
    ];

    readonly scenarioGroupOptions: Array<{ label: string; value: ScenarioGroup }> = [
        { label: 'Total', value: 'total' },
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' }
    ];

    baselineYear = 2026;
    projectedYear = 2031;

    htImpactCounts: Record<HtImpactKey, ImpactRow> = {
        undiagnosed: this.zero(),
        diagnosed: this.zero(),
        treated: this.zero(),
        controlled: this.zero()
    };

    cvdSummary: Array<{
        id: 'events' | 'patients' | 'deaths';
        title: string;
        iconClass: string;
        baseline: number;
        projected: number;
        deltaPct: number;
        deltaAbs: number;
    }> = [];

    ngOnInit(): void {
        this.scenarioChartOptions = this.buildScenarioChartOptions();

        combineLatest([this.store.runsList$, this.store.selectedRunId$])
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.recomputeAll();
                this.cdr.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['selectedInfoPeriod'] ||
            changes['selectedInfoSimulation'] ||
            changes['selectedCascadeSimulation']
        ) {
            this.recomputeAll();
            this.cdr.markForCheck();
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    get htImpactRows(): HtImpactViewItem[] {
        return [
            {
                key: 'undiagnosed',
                title: 'Undiagnosed',
                icon: 'pi pi-search',
                row: this.htImpactCounts.undiagnosed
            },
            {
                key: 'diagnosed',
                title: 'Diagnosed',
                icon: 'pi pi-verified',
                row: this.htImpactCounts.diagnosed
            },
            {
                key: 'treated',
                title: 'Treated',
                icon: 'pi pi-heart',
                row: this.htImpactCounts.treated
            },
            {
                key: 'controlled',
                title: 'Controlled',
                icon: 'pi pi-check-circle',
                row: this.htImpactCounts.controlled
            }
        ];
    }

    get htNote(): string {
        const baselineSelected = this.isBaseline(this.selectedInfoSimulation);

        return baselineSelected
            ? 'This represents the total number of individuals in each hypertension category over the selected period under the baseline scenario.'
            : 'This represents the difference in each hypertension category compared to the baseline scenario.';
    }

    get cvdNote(): string {
        const baselineSelected = this.isBaseline(this.selectedInfoSimulation);

        return baselineSelected
            ? 'This represents the total number of CVD events, patients, and deaths over the selected period under the baseline scenario.'
            : 'This represents the difference in CVD outcomes compared to the baseline scenario.';
    }

    setCascadeView(view: CascadeView): void {
        this.cascadeView = view;

        if (view === 'scenarios') {
            this.buildScenarioChart();
        }

        this.cdr.markForCheck();
    }

    onScenarioMetricChange(): void {
        this.buildScenarioChart();
        this.cdr.markForCheck();
    }

    onScenarioGroupChange(): void {
        this.buildScenarioChart();
        this.cdr.markForCheck();
    }

    private recomputeAll(): void {
        const baselineRun = this.store.getBaselineOrFirst();

        if (!baselineRun?.t3) {
            this.resetAll();
            return;
        }

        const scenarioRun = this.store.getRunByTitle(this.selectedInfoSimulation) ?? baselineRun;

        const startYear = 2026;
        const yearsForward = this.parseYearsForward(this.selectedInfoPeriod);
        const desiredTargetYear = startYear + yearsForward;
        const baselineSelected = this.isBaseline(this.selectedInfoSimulation);

        const baseYear = this.pickNearestYear(
            baselineRun.t3,
            baselineSelected ? startYear : desiredTargetYear
        );

        const scenarioYear = this.pickNearestYear(scenarioRun.t3, desiredTargetYear);

        this.baselineYear = baseYear;
        this.projectedYear = scenarioYear;

        this.computeComparison(baselineRun.t3, scenarioRun.t3, baseYear, scenarioYear);
        this.initCascadeChart(baselineRun.t3, scenarioRun.t3, scenarioYear);
        this.buildScenarioChart();
    }

    private computeComparison(
        baselineT3: T3ApiResponse,
        scenarioT3: T3ApiResponse,
        baseYear: number,
        scenarioYear: number
    ): void {
        const undBase = this.store.pick(baselineT3, 'Undiagnosed', baseYear);
        const diagBase = this.store.pick(baselineT3, 'Diagnosed', baseYear);
        const trBase = this.store.pick(baselineT3, 'Treated', baseYear);
        const ctrlBase = this.store.pick(baselineT3, 'Controlled', baseYear);

        const undNew = this.store.pick(scenarioT3, 'Undiagnosed', scenarioYear);
        const diagNew = this.store.pick(scenarioT3, 'Diagnosed', scenarioYear);
        const trNew = this.store.pick(scenarioT3, 'Treated', scenarioYear);
        const ctrlNew = this.store.pick(scenarioT3, 'Controlled', scenarioYear);

        this.htImpactCounts = {
            undiagnosed: this.deltaRow(undBase, undNew),
            diagnosed: this.deltaRow(diagBase, diagNew),
            treated: this.deltaRow(trBase, trNew),
            controlled: this.deltaRow(ctrlBase, ctrlNew)
        };

        const evBase = this.store.pick(baselineT3, 'CVD_Events', baseYear);
        const ptBase = this.store.pick(baselineT3, 'CVD_Patients', baseYear);
        const deBase = this.store.pick(baselineT3, 'CVD_Deaths', baseYear);

        const evNew = this.store.pick(scenarioT3, 'CVD_Events', scenarioYear);
        const ptNew = this.store.pick(scenarioT3, 'CVD_Patients', scenarioYear);
        const deNew = this.store.pick(scenarioT3, 'CVD_Deaths', scenarioYear);

        this.cvdSummary = [
            this.deltaItem('events', 'CVD Events', 'pi pi-chart-bar', evBase, evNew),
            this.deltaItem('patients', 'CVD Patients', 'pi pi-users', ptBase, ptNew),
            this.deltaItem('deaths', 'CVD Deaths', 'pi pi-exclamation-circle', deBase, deNew)
        ];
    }

    private initCascadeChart(baselineT3: T3ApiResponse, scenarioT3: T3ApiResponse, scenarioYear: number): void {
        const labels = ['Undiagnosed', 'Diagnosed', 'Treated', 'Controlled'];

        const baseline = [
            this.toMillions(this.store.pick(baselineT3, 'Undiagnosed', scenarioYear)),
            this.toMillions(this.store.pick(baselineT3, 'Diagnosed', scenarioYear)),
            this.toMillions(this.store.pick(baselineT3, 'Treated', scenarioYear)),
            this.toMillions(this.store.pick(baselineT3, 'Controlled', scenarioYear))
        ];

        const scenario = [
            this.toMillions(this.store.pick(scenarioT3, 'Undiagnosed', scenarioYear)),
            this.toMillions(this.store.pick(scenarioT3, 'Diagnosed', scenarioYear)),
            this.toMillions(this.store.pick(scenarioT3, 'Treated', scenarioYear)),
            this.toMillions(this.store.pick(scenarioT3, 'Controlled', scenarioYear))
        ];

        const baselineSelected =
            this.isBaseline(this.selectedInfoSimulation) ||
            this.isBaseline(this.selectedCascadeSimulation);

        const datasets = baselineSelected
            ? [{ label: 'Basic Simulation', backgroundColor: '#fec2d6', data: baseline, barThickness: 12 }]
            : [
                { label: 'Basic Simulation', backgroundColor: '#fec2d6', data: baseline, barThickness: 12 },
                {
                    label: this.selectedCascadeSimulation || 'Selected Simulation',
                    backgroundColor: '#d41c58',
                    data: scenario,
                    barThickness: 12
                }
            ];

        this.cascadeData = { labels, datasets };

        this.cascadeOptions = {
            responsive: true,
            maintainAspectRatio: false,
            aspectRatio: 1,
            layout: {
                padding: {
                    top: 12,
                    right: 12,
                    bottom: 4,
                    left: 4
                }
            },
            plugins: {
                legend: { display: true, labels: { color: '#64748b', font: { size: 11 } } },
                tooltip: {
                    callbacks: {
                        label: (ctx: any) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(2)}M`
                    }
                }
            },
            scales: {
                x: {
                    ticks: { color: '#64748b', font: { size: 11 } },
                    grid: { display: false }
                },
                y: {
                    ticks: {
                        color: '#64748b',
                        font: { size: 11 },
                        callback: (value: any) => `${value}M`
                    },
                    grid: { color: '#e2e8f0', drawBorder: false }
                }
            },
        };
    }

    private buildScenarioChart(): void {
        const runs = this.store.getRunsSnapshot();

        if (!runs.length) {
            this.scenarioChartData = { datasets: [] };
            this.scenarioXMin = 2026;
            this.scenarioXMax = 2046;
            this.scenarioSuggestedMax = 1;
            this.scenarioChartOptions = this.buildScenarioChartOptions();
            return;
        }

        const allYears = runs
            .flatMap((run) => Array.isArray(run?.t3?.years) ? run.t3.years : [])
            .map(Number)
            .filter(Number.isFinite)
            .sort((a, b) => a - b);

        const xMin = allYears.length ? allYears[0] : 2026;
        const xMax = allYears.length ? allYears[allYears.length - 1] : 2046;

        this.scenarioXMin = xMin;
        this.scenarioXMax = xMax;

        const colors = ['#4f46e5', '#d41c58', '#2563eb', '#059669', '#f59e0b', '#7c3aed', '#0891b2'];

        const datasets = runs.map((run, index) => {
            const color = colors[index % colors.length];
            const years = Array.isArray(run?.t3?.years)
                ? run.t3.years.map(Number).filter(Number.isFinite)
                : [];

            return {
                label: run.title,
                data: years.map((year) => ({
                    x: year,
                    y: this.pickScenarioMetricValue(run.t3, this.scenarioMetric, this.scenarioGroup, year)
                })),
                borderColor: color,
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 8,
                tension: 0.3,
                fill: false
            };
        });

        const allValues = datasets
            .flatMap((dataset) => dataset.data)
            .map((point) => Number(point.y))
            .filter(Number.isFinite);

        const maxValue = Math.max(...allValues, 1);
        this.scenarioSuggestedMax = Math.ceil(maxValue * 1.08);

        this.scenarioChartData = {
            datasets
        };

        this.scenarioChartOptions = this.buildScenarioChartOptions();
    }

    private pickScenarioMetricValue(
        t3: T3ApiResponse,
        metric: ScenarioMetric,
        group: ScenarioGroup,
        year: number
    ): number {
        const key = this.scenarioMetricKey(metric, group);
        const value = this.store.pick(t3, key, year);

        if (value > 0 || group === 'total') {
            return value;
        }

        return this.store.pick(t3, metric, year);
    }

    private scenarioMetricKey(metric: ScenarioMetric, group: ScenarioGroup): string {
        if (group === 'male') {
            return `${metric}_Male`;
        }

        if (group === 'female') {
            return `${metric}_Female`;
        }

        return metric;
    }

    private buildScenarioChartOptions(): any {
        const rangeYears = Math.abs(this.scenarioXMax - this.scenarioXMin);
        const isShortRange = rangeYears < 10;
        const targetTicks = 6;
        const stepForLong = Math.max(1, Math.round(rangeYears / (targetTicks - 1)));

        return {
            responsive: true,
            maintainAspectRatio: true,
            parsing: true,
            layout: {
                padding: {
                    top: 8,
                    right: 12,
                    bottom: 4,
                    left: 4
                }
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                    labels: {
                        color: 'var(--text-color, #334155)',
                        usePointStyle: true,
                        boxWidth: 10,
                        font: { size: 10 },
                        padding: 14
                    }
                },
                tooltip: {
                    mode: 'nearest',
                    intersect: false,
                    backgroundColor: '#0f172a',
                    titleColor: '#ffffff',
                    bodyColor: '#ffffff',
                    cornerRadius: 10,
                    padding: 12,
                    callbacks: {
                        title: (items: any[]) => `Year: ${String(items?.[0]?.raw?.x ?? '')}`,
                        label: (ctx: any) => {
                            const value = Number(ctx.raw?.y ?? 0);
                            const groupLabel = this.scenarioGroupOptions.find((x) => x.value === this.scenarioGroup)?.label ?? 'Total';
                            return `${ctx.dataset.label} ${groupLabel}: ${new Intl.NumberFormat('en-US').format(value)}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    type: 'linear',
                    min: this.scenarioXMin,
                    max: this.scenarioXMax,
                    ticks: {
                        stepSize: isShortRange ? 1 : stepForLong,
                        autoSkip: false,
                        precision: 0,
                        color: 'var(--text-secondary-color, #64748B)',
                        callback: (value: number) => String(Math.round(Number(value))),
                        font: { size: 10 }
                    },
                    grid: {
                        color: 'rgba(100,116,139,0.1)'
                    },
                    border: {
                        color: 'rgba(100,116,139,0.18)'
                    }
                },
                y: {
                    beginAtZero: true,
                    suggestedMax: this.scenarioSuggestedMax,
                    ticks: {
                        color: 'var(--text-secondary-color, #64748B)',
                        callback: (value: number) =>
                            new Intl.NumberFormat('en-US', {
                                notation: 'compact',
                                maximumFractionDigits: 1
                            }).format(Number(value || 0)),
                        font: { size: 10 }
                    },
                    grid: {
                        color: 'rgba(100,116,139,0.1)'
                    },
                    border: {
                        display: false
                    }
                }
            }
        };
    }

    private deltaRow(baseline: number, projected: number): ImpactRow {
        const deltaAbs = projected - baseline;
        const deltaPct = baseline !== 0 ? Math.round((deltaAbs / baseline) * 100) : 0;

        return {
            baseline,
            new: projected,
            deltaAbs: Math.round(deltaAbs),
            deltaPct
        };
    }

    private deltaItem(
        id: 'events' | 'patients' | 'deaths',
        title: string,
        iconClass: string,
        baseline: number,
        projected: number
    ) {
        const deltaAbs = projected - baseline;
        const deltaPct = baseline !== 0 ? Math.round((deltaAbs / baseline) * 100) : 0;

        const trendClass =
            deltaAbs < 0
                ? 'text-emerald-600'
                : deltaAbs > 0
                    ? 'text-rose-600'
                    : 'text-slate-500';

        return {
            id,
            title,
            iconClass: `${iconClass} ${trendClass}`,
            baseline,
            projected,
            deltaPct,
            deltaAbs: Math.round(deltaAbs)
        };
    }

    private pickNearestYear(t3: T3ApiResponse, desired: number): number {
        const years = Array.isArray(t3?.years) ? t3.years.map(Number).filter(Number.isFinite) : [];

        if (!years.length) return desired;
        if (years.includes(desired)) return desired;

        return years[years.length - 1];
    }

    private parseYearsForward(period: string): number {
        const p = String(period || '').trim();

        if (p.startsWith('20')) return 20;
        if (p.startsWith('15')) return 15;
        if (p.startsWith('10')) return 10;

        return 5;
    }

    private zero(): ImpactRow {
        return {
            baseline: 0,
            new: 0,
            deltaAbs: 0,
            deltaPct: 0
        };
    }

    private resetAll(): void {
        this.baselineYear = 2026;
        this.projectedYear = 2031;

        this.htImpactCounts = {
            undiagnosed: this.zero(),
            diagnosed: this.zero(),
            treated: this.zero(),
            controlled: this.zero()
        };

        this.cvdSummary = [];

        this.cascadeData = { labels: [], datasets: [] };
        this.cascadeOptions = {};

        this.scenarioChartData = { labels: [], datasets: [] };
    }

    private toMillions(value: number): number {
        return Number.isFinite(value) ? Number((value / 1_000_000).toFixed(2)) : 0;
    }

   private isBaseline(title: string): boolean {
  const t = String(title || '').trim().toLowerCase();
  return t === 'basic simulation' || t === 'baseline';
}

    private withAlpha(hex: string, alpha: number): string {
        const clean = hex.replace('#', '');
        const [r, g, b] = [0, 2, 4].map((i) => parseInt(clean.slice(i, i + 2), 16));
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    format(value: number): string {
        return new Intl.NumberFormat('en-US').format(Math.ceil(Number(value || 0)));
    }
}